// @flow

import Dispatcher from 'dispatcher';
import ApiClient from 'utils/ApiClient';
import * as ActionCUType from 'action_types/CurrentUserActionTypes';
import CurrentUser from 'models/CurrentUser';
import EmailConfirmation from 'actions/NotifyConfirmationActions';
import CurrentUserAction from 'actions/CurrentUserActions';

const UpdateCsrfToken = (csrf_token: string): void => {
  let csrf = document.getElementsByName('csrf-token');
  csrf[0].content = csrf_token;
};

const RequestCurrentUser = (success: () => void): void => {
  ApiClient.get({
    path: '/users/current.json',
    success: response => {
      Dispatcher.dispatch({
        type: ActionCUType.CURRENT_USER_LOADED,
        currentUser: new CurrentUser(response)
      });
      success(response);
      CurrentUserAction.startCurrentUserPolling();
    },
    error: error => {
      Dispatcher.dispatch({
        type: ActionCUType.CURRENT_USER_LOAD_ERROR,
        error
      });
    }
  });
};

class AuthenticationAction {
  loginUser(data: {}, success?: () => void, error?: () => void): Promise<any> {
    return ApiClient.post({
      path: '/users/sign_in',
      jsonData: data,
      success: response => {
        UpdateCsrfToken(response.csrf);
        if (success !== undefined) {
          RequestCurrentUser(success);
        }
      },
      error: error,
      skipApiUrl: true,
      skipRedirect: true
    });
  }

  sendRestorePasswordEmail(
    data: {},
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: '/users/password',
      data: data,
      success: success,
      error: error,
      skipRedirect: true,
      skipApiUrl: true
    });
  }

  restorePassword(
    data: {},
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.put({
      path: '/users/password.json',
      data: data,
      skipApiUrl: true,
      skipRedirect: true,
      success: success,
      error: error
    });
  }

  registerUser(
    data: {},
    emailConfirmation: boolean,
    success?: () => void,
    error?: () => void,
    onRegistered?: (response: any) => void
  ): Promise<any> {
    return ApiClient.post({
      path: '/users.json',
      data: data,
      success: response => {
        UpdateCsrfToken(response.csrf);
        if (emailConfirmation) {
          EmailConfirmation.setEmailConfirmation(response.id, response.email);
        }
        // Runs synchronously on registration success, BEFORE RequestCurrentUser
        // dispatches the current-user load that triggers the redirect. Use this
        // for events that must be queued before navigation (e.g. the
        // Registration Completed beacon), which otherwise loses the race.
        if (onRegistered !== undefined) {
          onRegistered(response);
        }
        if (success !== undefined) {
          RequestCurrentUser(success);
        }
      },
      skipRedirect: true,
      skipApiUrl: true,
      error: error
    });
  }

  logout(success?: () => void, skipDispatch = false): Promise<any> {
    return ApiClient.delete({
      path: '/logout.json',
      skipApiUrl: true,
      success: response => {
        if (response && response.csrf) {
          UpdateCsrfToken(response.csrf);
        }
        if (!skipDispatch) {
          Dispatcher.dispatch({
            type: ActionCUType.CURRENT_USER_REMOVE
          });
        }
        if (success !== undefined) {
          success(response);
        }
      }
    });
  }

  backLogin(success: () => void): Promise<any> {
    return ApiClient.delete({
      path: '/admin_logins/back_admin.json',
      skipRedirect: true,
      skipApiUrl: true,
      success
    });
  }
}

export default new AuthenticationAction();
